<template>
    <div>
        <h1 class="text-2xl font-bold mb-8">Cancel your Subscription</h1>
        <BillingSection title="Current Subscription">
            <div
                v-if="errorMessage"
                role="alert"
                class="flex flex-col gap-2 rounded-md bg-red-50 p-4 mb-4 text-red-700"
            >
                <p class="font-bold">{{ errorTitle }}</p>
                <p class="text-sm">{{ errorMessage }}</p>
            </div>
            <div
                class="space-y-2.5 bg-blue-50 p-6"
                v-if="subscription !== null"
            >
                <div class="plan-details flex flex-col">
                    <div class="font-bold text-xl mt-2">
                        {{ subscription.title }}
                    </div>
                    <div class="flex-grow my-2"></div>
                    <div class="text-lg">
                        ${{ formatPrice(subscription.price) }} per month
                    </div>
                    <p class="text-small tracking-tight my-2">
                        Your plan will be canceled, but is still available until
                        the end of your billing period on
                        <strong>{{
                            formatDate(subscription.current_period_end)
                        }}</strong
                        >.
                    </p>
                    <p class="text-small tracking-tight my-2">
                        If you change your mind, you can renew your
                        subscription.
                    </p>
                    <div
                        class="flex flex-col sm:flex-row items-center flex-1 gap-2 mt-6"
                    >
                        <Link
                            href="/account/billing"
                            intent="primary"
                            class="w-full sm:w-auto"
                        >
                           Keep Subscription
                        </Link>
                        <button
                            class="w-full sm:w-auto text-center flex gap-2 items-center justify-center button rounded-md bg-blue-100 hover:bg-blue-200 border-none"
                            :disabled="processing"
                            v-on:click="showCancelSubscriptionModal"
                        >
                            Cancel Subscription
                            <LoadingSpinner
                                color="light"
                                placement="button"
                                :show="processing"
                            />
                        </button>
                    </div>
                    <div class="mt-4">
                        <p class="text-xs tracking-tight">
                            By canceling your plan, you agree to Clover
                            Learning's
                            <strong
                                ><a
                                    href="https://cloverlearning.com/terms-of-service"
                                    target="_blank"
                                    >Terms of Service</a
                                ></strong
                            >
                            and
                            <strong
                                ><a
                                    href="https://cloverlearning.com/privacy"
                                    target="_blank"
                                    >Privacy Policy</a
                                ></strong
                            >.
                        </p>
                    </div>
                </div>
            </div>
        </BillingSection>
        <CancelSubscriptionModal
            :active="showCancelModal"
            @close="closeCancelModal"
            @confirm="cancelPlan"
            :csrf-token="csrfToken"
        />
        <PassedCertificationUpsellModal @decline="proceedWithCancellation" />
        <SaveOfferUpsellModal @decline="proceedWithCancellation" />
        <SwitchPlanUpsellModal @decline="proceedWithCancellation" />
    </div>
</template>

<script lang="ts">
import { mapState } from "vuex";
import BillingSection from "./BillingSection.vue";
import {
    cancelSubscription,
    getSubscription,
    renewSubscription,
} from "../../../api/billing";
import { format, parseISO } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import Link from "@/components/ui/ButtonLinks/Link.vue";
import CancelSubscriptionModal from "@/components/_elements/modals/CancelSubscriptionModal.vue";
import PassedCertificationUpsellModal from "@/components/_elements/modals/PassedCertificationUpsellModal.vue";
import SaveOfferUpsellModal from "@/components/_elements/modals/SaveOfferUpsellModal.vue";
import SwitchPlanUpsellModal from "@/components/_elements/modals/SwitchPlanUpsellModal.vue";
import { usePassedCertUpsellModalStore } from "@/stores/passedCertUpsellModalStore";
import { useSaveOfferModalStore } from "@/stores/saveOfferModalStore";
import type { SaveOfferVariant } from "@/stores/saveOfferModalStore";
import { useSwitchPlanModalStore } from "@/stores/switchPlanModalStore";
import { analytics } from "@/services/segment.ts";
import { getApiErrorMessage } from "@/api/errors";

export default {
    components: { BillingSection, Link, CancelSubscriptionModal, PassedCertificationUpsellModal, SaveOfferUpsellModal, SwitchPlanUpsellModal },
    props: {
        csrfToken: {
            type: String,
            required: true
        }
    },
    data(): any {
        return {
            subscriptionId:
                // @ts-ignore
                window.location.pathname.match(/sub_[a-zA-Z0-9]+/)[0],
            subscription: null,
            processing: false,
            showCancelModal: false,
            errorTitle: "",
            errorMessage: null,
        };
    },
    computed: {
        ...mapState(["user"]),
    },
    methods: {
        formatPrice(price) {
            return (price / 100).toFixed(2);
        },
        formatDate(date) {
            let addChar = date.charAt(date.length - 1) === "Z" ? "" : "Z";
            return format(
                utcToZonedTime(parseISO(date + addChar), this.user.timezone),
                "MMMM d, yyyy"
            );
        },
        showCancelSubscriptionModal() {
            analytics.track("D2C Cancel Flow Initiated", {
                current_plan: this.user.subscription_plan,
                current_term: this.user.subscription_frequency,
                entry_point: "dashboard",
            });
            this.showCancelModal = true;
        },
        closeCancelModal() {
            this.showCancelModal = false;
        },
        getReasonId(surveyAnswers, survey): string {
            const texts = this.getSelectedAnswerTexts(surveyAnswers, survey);
            if (texts.some((t) => t.includes("passed my certification"))) return "passed_certification";
            if (texts.some((t) => t.includes("did not pass"))) return "did_not_pass_exam";
            if (texts.some((t) => t.includes("cost of subscription"))) return "cost_of_subscription";
            if (texts.some((t) => t.includes("other"))) return "other";
            return "unknown";
        },
        cancelPlan(surveyAnswers = {}, survey = null) {
            if (survey) {
                const reasonId = this.getReasonId(surveyAnswers, survey);
                const reasonLabel = this.getSelectedAnswerTexts(surveyAnswers, survey)[0] ?? null;
                analytics.track("D2C Cancel Reason Selected", {
                    reason_id: reasonId,
                    reason_label: reasonLabel,
                });

                const freeformEntry = Object.entries(surveyAnswers).find(
                    ([key, val]) => key.includes("_freeform") && val,
                );
                if (freeformEntry) {
                    analytics.track("D2C Cancel Reason Other Text Submitted", {
                        text_length: String(freeformEntry[1]).length,
                    });
                }
            }

            if (survey && this.isPassedCertificationAnswer(surveyAnswers, survey)) {
                this.showCancelModal = false;
                const upsellStore = usePassedCertUpsellModalStore();
                upsellStore.open();
                return;
            }

            const saveOfferVariant = survey ? this.getSaveOfferVariant(surveyAnswers, survey) : null;
            if (saveOfferVariant && this.isOnTrainingAndExamPrep()) {
                this.showCancelModal = false;
                const saveOfferStore = useSaveOfferModalStore();
                saveOfferStore.open(saveOfferVariant);
                return;
            }

            if (saveOfferVariant || (survey && this.isOtherAnswer(surveyAnswers, survey))) {
                this.showCancelModal = false;
                const switchPlanStore = useSwitchPlanModalStore();
                switchPlanStore.open();
                return;
            }

            this.proceedWithCancellation();
        },
        getSelectedAnswerTexts(surveyAnswers, survey): string[] {
            if (!survey?.survey_blueprint?.questions) return [];

            return Object.entries(surveyAnswers)
                .filter(([questionId]) => !questionId.includes('_freeform'))
                .map(([questionId, answerId]) => {
                    const question = survey.survey_blueprint.questions.find(
                        (q) => q.id === parseInt(questionId)
                    );
                    const answer = question?.answers.find((a) => a.id === answerId);
                    return answer?.answer?.toLowerCase() ?? null;
                })
                .filter((text): text is string => text !== null);
        },
        isPassedCertificationAnswer(surveyAnswers, survey) {
            return this.getSelectedAnswerTexts(surveyAnswers, survey)
                .some(text => text.includes('passed my certification'));
        },
        isOtherAnswer(surveyAnswers, survey): boolean {
            return this.getSelectedAnswerTexts(surveyAnswers, survey)
                .some(text => text.includes('other'));
        },
        getSaveOfferVariant(surveyAnswers, survey): SaveOfferVariant | null {
            const texts = this.getSelectedAnswerTexts(surveyAnswers, survey);
            if (texts.some(t => t.includes('cost of subscription'))) return 'cost';
            if (texts.some(t => t.includes('did not pass'))) return 'failed';
            return null;
        },
        isOnTrainingAndExamPrep(): boolean {
            return this.user?.subscription_plan === 'student';
        },
        proceedWithCancellation() {
            analytics.track("D2C Cancel Cancellation Requested");

            this.processing = true;
            this.showCancelModal = false;
            this.errorMessage = null;

            cancelSubscription(this.user.uuid, this.subscriptionId)
                .then((subscription) => {
                    this.processing = false;
                    analytics.track("D2C Cancel Cancellation Confirmed", {
                        subscription_end_date: subscription?.current_period_end ?? null,
                    });
                    // @ts-ignore
                    window.location = "/account/surveys/offboarding/success";
                })
                .catch((e) => {
                    this.processing = false;
                    this.errorTitle = "We couldn't cancel your subscription.";
                    this.errorMessage = getApiErrorMessage(e);
                });
        },
    },
    mounted() {
        getSubscription(this.user.uuid, this.subscriptionId)
            .then((subscription) => {
                this.subscription = subscription;
            })
            .catch((e) => {
                this.errorTitle = "We couldn't load your subscription details.";
                this.errorMessage = getApiErrorMessage(e);
            });
    },
};
</script>
